import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { layoutFlow, parseFlow } from '../types';
import type { BotAction, BotFlow, CatalogProduct, FlowDefinition, FlowNode, FlowNodeType, FlowReport } from '../types';
import type { RehearsalTurn, RehearseRequest } from '../api';
import { FlowMap } from './FlowMap';
import { NodeEditor } from './NodeEditor';
import { FlowEmulator } from './FlowEmulator';
import { NODE_EXPLAIN, NODE_LABEL, NODE_ORDER, NODE_TONE, blankNode, nodeSummary } from './nodeKinds';
import { Confirm } from './Confirm';
import type { TestAction } from './TryAction';

type View = 'steps' | 'map' | 'settings' | 'json';

/** What the editor may change about a flow besides its definition. */
export interface FlowPatch {
  definition?: string;
  name?: string;
  channelId?: number | null;
  channelSpecified?: boolean;
}

/**
 * The calls this editor makes.
 *
 * Taken as a prop rather than imported, because the same editor is the client's bot
 * builder and PSM's. The two reach different routes — a client's are scoped to their
 * own tenant by the server, PSM's address any tenant by id — and the difference is
 * entirely in the URL. Passing it in keeps that difference in one small adapter per
 * app instead of a second copy of this file, which is where the two would quietly
 * stop being the same builder.
 */
export interface FlowStudioApi {
  validate: (definition: string) => Promise<FlowReport>;
  save: (id: number, patch: FlowPatch) => Promise<unknown>;
  publish: (id: number) => Promise<unknown>;
  rehearse: (body: RehearseRequest) => Promise<RehearsalTurn>;
  /** Deletes a draft. Absent, the Settings view has no delete. */
  remove?: (id: number) => Promise<unknown>;
  /** Calls an action once so a step can pick what to remember from a real answer. */
  testAction?: TestAction;
  /** The catalogue, for a "show products" step's picker. Absent, the step takes ids typed in. */
  products?: () => Promise<CatalogProduct[]>;

  /**
   * Names the new steps offer as suggestions. Each is a name the client already sees
   * on another screen, so the field stays a plain text box — typing a name that does
   * not exist is caught when the flow is validated, not hidden behind a dropdown that
   * cannot be loaded.
   */
  names?: FlowNameHints;
}

/** Suggestions for the steps that refer to something by name. */
export interface FlowNameHints {
  /** Approved templates, for a "send a template" step. */
  templates?: string[];
  /** The tenant's tags, for a "label the chat" step. */
  tags?: string[];
  /** Other bots, for a "go to another bot" step. */
  flows?: string[];
}

/** A number the flow can be tied to, in the words the Settings view shows. */
export interface FlowChannelOption { id: number; label: string }

const BUTTON = 'rounded-lg border border-(--color-line) px-3.5 py-2 text-body transition hover:bg-(--color-sunken) disabled:opacity-40 disabled:hover:bg-transparent';
const PRIMARY = 'rounded-lg bg-(--color-brand) px-4 py-2 text-body font-medium text-(--color-brand-ink) transition hover:bg-(--color-brand-strong) disabled:opacity-40';
const FIELD = 'mt-1 w-full rounded-lg border border-(--color-line) bg-(--color-paper) px-3 py-2 text-body focus-visible:outline-none focus-visible:border-(--color-brand) disabled:opacity-60';

/**
 * One flow: a workspace, not a form.
 *
 * Two panes. The left is the flow — as steps, as a map, its settings, or the
 * definition itself — and the right is a conversation with it. That split is the
 * whole design: a bot is built by editing one thing and immediately saying hello to
 * it, and a builder that makes you publish before you can see what you built is a
 * builder people stop using.
 *
 * The Steps view is an outline and one step, not a stack of every step. The Carysil
 * flow has sixty-four; a page of sixty-four open forms with the selected one hoisted
 * to the top (which is what this used to do) is a page nobody can find anything on.
 *
 * All four views edit the same definition and all of them are checked by the same
 * server call that decides whether it can be published, so there is no view in which
 * a flow can be made invalid quietly.
 */
export function FlowEditor({ flow, actions, api, channels, onClose }: {
  flow: BotFlow;
  actions: BotAction[];
  api: FlowStudioApi;
  channels?: FlowChannelOption[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [view, setView] = useState<View>('steps');
  // The definition and its history in one value, so an edit, an undo and a redo are
  // each one pure update rather than three states kept in step by hand.
  const [hist, setHist] = useState<{ text: string; past: string[]; future: string[]; at: number; tag: string | null }>(
    () => ({ text: flow.definition, past: [], future: [], at: 0, tag: null }));
  const text = hist.text;
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [report, setReport] = useState<FlowReport | null>(null);
  const [showTest, setShowTest] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [meta, setMeta] = useState({ name: flow.name, channelId: flow.channelId });

  const readOnly = flow.isPublished;
  const definition = useMemo(() => parseFlow(text), [text]);
  const nodes = definition.nodes ?? {};
  const textDirty = text !== flow.definition;
  const metaDirty = meta.name.trim() !== flow.name || meta.channelId !== flow.channelId;
  const dirty = textDirty || metaDirty;

  // Whether the text is even JSON, separately from whether the flow is valid. The two
  // are different failures and reporting them the same way is why a stray comma used
  // to read as "your flow has no steps".
  const parses = useMemo(() => {
    try { JSON.parse(text); return true; } catch { return false; }
  }, [text]);

  // Held in a ref so the debounce below depends on the text and nothing else. A caller
  // that rebuilds its api object each render — the obvious way to write one — would
  // otherwise restart the timer on every keystroke's re-render.
  const latest = useRef(api);
  latest.current = api;

  useEffect(() => {
    if (!parses) { setReport(null); return; }
    const id = setTimeout(() => {
      latest.current.validate(text).then(setReport).catch(() => setReport(null));
    }, 400);
    return () => clearTimeout(id);
  }, [text, parses]);

  // Leaving the page with unsaved work. The in-app back button asks below; this is
  // the browser's own close and refresh, which cannot be asked nicely.
  useEffect(() => {
    if (!dirty) return;
    const guard = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [dirty]);

  // ---------------------------------------------------------------- history --

  /**
   * Every change goes through here, so every change can be undone.
   *
   * `tag` names what is being typed into — one step's one field, the definition
   * box, the settings. Consecutive edits with the same tag, close together, share a
   * history entry, so undo steps back a burst of typing rather than one letter.
   * Structural edits (add, rename, remove, a new link) carry no tag and are never
   * merged: undoing "add a step" must not also undo the rename before it.
   */
  const setText = useCallback((next: string, tag: string | null = null) => {
    setHist((h) => {
      if (next === h.text) return h;
      const now = Date.now();
      const burst = tag !== null && tag === h.tag && now - h.at < 1500;
      return { text: next, past: burst ? h.past : [...h.past.slice(-149), h.text], future: [], at: now, tag };
    });
  }, []);

  const undo = () => setHist((h) => {
    const previous = h.past[h.past.length - 1];
    return previous === undefined ? h : { text: previous, past: h.past.slice(0, -1), future: [h.text, ...h.future], at: 0, tag: null };
  });

  const redo = () => setHist((h) => {
    const next = h.future[0];
    return next === undefined ? h : { text: next, past: [...h.past, h.text], future: h.future.slice(1), at: 0, tag: null };
  });

  // Ctrl+Z outside a text box. Inside one, the browser's own undo is the right one.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'z') return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      e.preventDefault();
      if (e.shiftKey) redo(); else undo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ------------------------------------------------------------------ edits --

  const write = (next: FlowDefinition, tag: string | null = null) => setText(JSON.stringify(next, null, 2), tag);

  const patchNode = (id: string, node: FlowNode) => {
    // Which fields changed, so typing into one of them coalesces in the history.
    const before = nodes[id] ?? {};
    const changed = [...new Set([...Object.keys(before), ...Object.keys(node)])]
      .filter((key) => JSON.stringify(before[key]) !== JSON.stringify(node[key]));
    write({ ...definition, nodes: { ...nodes, [id]: node } }, `${id}.${changed.join(',')}`);
  };

  const renameNode = (from: string, to: string) => {
    if (!to || to === from || nodes[to]) return;

    // Every reference is rewritten with the name. A rename that left the old id in
    // half a dozen transitions would produce exactly the dangling targets this editor
    // exists to make impossible.
    const renamed: Record<string, FlowNode> = {};
    for (const [id, node] of Object.entries(nodes)) {
      const copy: FlowNode = { ...node };
      for (const key of ['next', 'default', 'empty_next']) {
        if (copy[key] === from) copy[key] = to;
      }
      for (const key of ['transitions', 'cases']) {
        const map = copy[key] as Record<string, string> | undefined;
        if (map) copy[key] = Object.fromEntries(Object.entries(map).map(([k, v]) => [k, v === from ? to : v]));
      }
      if (Array.isArray(copy.extra_rows)) {
        copy.extra_rows = (copy.extra_rows as Record<string, unknown>[])
          .map((e) => (e?.target === from ? { ...e, target: to } : e));
      }
      renamed[id === from ? to : id] = copy;
    }

    const resume = definition.resume && definition.resume.state === from
      ? { ...definition.resume, state: to }
      : definition.resume;

    write({ ...definition, nodes: renamed, start: definition.start === from ? to : definition.start, resume });
    setSelected(to);
  };

  const removeNode = (id: string) => {
    const rest = { ...nodes };
    delete rest[id];
    write({ ...definition, nodes: rest, start: definition.start === id ? undefined : definition.start });
    if (selected === id) setSelected(null);
  };

  /** A name nothing else has, in the type's own words: message_2, menu_3. */
  const freshName = (type: FlowNodeType, taken: Record<string, unknown>) => {
    const base = type === 'list' ? 'menu' : type === 'dynamic_list' ? 'menu_from' : type === 'capture' ? 'ask' : type === 'condition' ? 'branch' : type;
    let name = base;
    let n = 1;
    while (taken[name]) name = `${base}_${++n}`;
    return name;
  };

  const addNode = (type: FlowNodeType) => {
    const name = freshName(type, nodes);
    write({
      ...definition,
      start: definition.start && nodes[definition.start] ? definition.start : name,
      nodes: { ...nodes, [name]: blankNode(type) },
    });
    setSelected(name);
    setView('steps');
  };

  /**
   * A step created from a "goes to", linked in the same write.
   *
   * `link` is the step editor's own patch — "this transition now points at the new
   * step" — applied to the current node in the same definition the new node is
   * added to. Two writes would race each other's stale copy of `definition`.
   */
  const createLinked = (from: string) => (link: (newId: string) => FlowNode) => {
    const name = freshName('message', nodes);
    write({ ...definition, nodes: { ...nodes, [from]: link(name), [name]: blankNode('message') } });
    setSelected(name);
  };

  // ------------------------------------------------------------------ server --

  const save = useMutation({
    mutationFn: () => latest.current.save(flow.id, {
      definition: textDirty ? text : undefined,
      name: metaDirty && meta.name.trim() ? meta.name.trim() : undefined,
      channelId: meta.channelId,
      channelSpecified: metaDirty && meta.channelId !== flow.channelId,
    }),
    onSuccess: () => {
      setError(null);
      setNote('Saved.');
      setTimeout(() => setNote(null), 2500);
      void queryClient.invalidateQueries({ queryKey: ['flow', flow.id] });
      void queryClient.invalidateQueries({ queryKey: ['bot-flow', flow.id] });
      void queryClient.invalidateQueries({ queryKey: ['flows'] });
      void queryClient.invalidateQueries({ queryKey: ['bot-flows'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save.'),
  });

  const publish = useMutation({
    mutationFn: () => latest.current.publish(flow.id),
    onSuccess: () => {
      setError(null);
      // Publishing changes this version (published, read-only) and creates the
      // next draft; both lists and this flow's own record are stale.
      void queryClient.invalidateQueries({ queryKey: ['flow', flow.id] });
      void queryClient.invalidateQueries({ queryKey: ['bot-flow', flow.id] });
      void queryClient.invalidateQueries({ queryKey: ['flows'] });
      void queryClient.invalidateQueries({ queryKey: ['bot-flows'] });
      onClose();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not publish.'),
  });

  const remove = useMutation({
    mutationFn: () => latest.current.remove!(flow.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['flows'] });
      void queryClient.invalidateQueries({ queryKey: ['bot-flows'] });
      onClose();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not delete.'),
  });

  // ------------------------------------------------------------------ derived --

  const errorCount = report?.errors.length ?? 0;
  const warnCount = report?.warnings.length ?? 0;

  /** Problems by step, for the outline's badges. */
  const problemsByNode = useMemo(() => {
    const map = new Map<string, { errors: number; warnings: number }>();
    const tally = (list: string[], key: 'errors' | 'warnings') => {
      for (const line of list) {
        const id = nodeOf(line, nodes);
        if (!id) continue;
        const entry = map.get(id) ?? { errors: 0, warnings: 0 };
        entry[key] += 1;
        map.set(id, entry);
      }
    };
    tally(report?.errors ?? [], 'errors');
    tally(report?.warnings ?? [], 'warnings');
    return map;
  }, [report, nodes]);

  /** Steps in reading order: by distance from the start, unreachable ones last. */
  const ordered = useMemo(() => {
    const laid = layoutFlow(definition).nodes;
    return [...laid].sort((a, b) =>
      a.unreachable !== b.unreachable ? (a.unreachable ? 1 : -1) : a.column - b.column || a.row - b.row);
  }, [definition]);

  // Something is always selected once there is anything to select: the start.
  const current = selected && nodes[selected] ? selected : ordered[0]?.id ?? null;

  const leave = () => { if (dirty) setLeaving(true); else onClose(); };

  return (
    <div className="flex flex-col gap-4">
      {/* ------------------------------------------------------------- header -- */}
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={leave} className={BUTTON}>← All bots</button>

        <div className="min-w-0">
          <h1 className="truncate text-title font-semibold tracking-tight">{meta.name || flow.name}</h1>
          <p className="text-small text-(--color-muted)">
            Version {flow.version}
            {readOnly ? ' · published, read only' : ' · draft'}
            {dirty ? ' · unsaved changes' : ''}
          </p>
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {!readOnly && (
            <div className="flex gap-1">
              <button type="button" onClick={undo} disabled={hist.past.length === 0} className={BUTTON} aria-label="Undo" title="Undo (Ctrl+Z)">↶</button>
              <button type="button" onClick={redo} disabled={hist.future.length === 0} className={BUTTON} aria-label="Redo" title="Redo (Ctrl+Shift+Z)">↷</button>
            </div>
          )}
          <button type="button" onClick={() => setShowTest((v) => !v)} className={`${BUTTON} xl:hidden`}>
            {showTest ? 'Hide test' : 'Test it'}
          </button>
          <button
            type="button"
            disabled={!dirty || save.isPending || readOnly || !parses}
            onClick={() => save.mutate()}
            title={!parses ? 'The definition is not valid JSON' : undefined}
            className={BUTTON}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            disabled={dirty || publish.isPending || readOnly || report?.ok === false}
            title={dirty ? 'Save first' : report?.ok === false ? 'Fix the errors first' : undefined}
            onClick={() => publish.mutate()}
            className={PRIMARY}
          >
            {publish.isPending ? 'Publishing…' : 'Publish this version'}
          </button>
        </div>
      </div>

      {error && (
        <p role="alert" className="rounded-lg border border-(--color-bad) bg-(--color-bad-wash) px-4 py-3 text-body text-(--color-bad)">
          {error}
        </p>
      )}
      {note && !error && (
        <p className="rounded-lg border border-(--color-good) bg-(--color-good-wash) px-4 py-3 text-body text-(--color-good)">{note}</p>
      )}

      {/* --------------------------------------------------------- two panes -- */}
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start">
        <div className="min-w-0 flex-1">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="flex gap-1 rounded-lg bg-(--color-sunken) p-1" role="tablist">
              {(['steps', 'map', 'settings', 'json'] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={view === value}
                  onClick={() => setView(value)}
                  className={[
                    'rounded-md px-3.5 py-1.5 text-body font-medium transition',
                    view === value ? 'bg-(--color-surface) text-(--color-ink) shadow-sm' : 'text-(--color-muted) hover:text-(--color-ink)',
                  ].join(' ')}
                >
                  {value === 'json' ? 'Definition' : value === 'map' ? 'Map' : value === 'settings' ? 'Settings' : 'Steps'}
                </button>
              ))}
            </div>

            {(errorCount > 0 || warnCount > 0) && parses && (
              <span className="text-small text-(--color-muted)">
                {errorCount > 0 && <span className="text-(--color-bad)">{errorCount} to fix</span>}
                {errorCount > 0 && warnCount > 0 && ' · '}
                {warnCount > 0 && `${warnCount} to look at`}
              </span>
            )}
          </div>

          <Problems parses={parses} report={report} nodes={nodes} onSelect={(id) => { setSelected(id); setView('steps'); }} />

          {view === 'steps' && (
            <div className="grid gap-3 md:grid-cols-[14rem_minmax(0,1fr)] 2xl:grid-cols-[16rem_minmax(0,1fr)] md:items-start">
              <Outline
                ordered={ordered}
                current={current}
                problems={problemsByNode}
                start={definition.start}
                readOnly={readOnly}
                onSelect={setSelected}
                onAdd={addNode}
              />

              {current && nodes[current] ? (
                <NodeEditor
                  key={current}
                  id={current}
                  node={nodes[current]!}
                  definition={definition}
                  actions={actions}
                  readOnly={readOnly}
                  onChange={(patch) => patchNode(current, patch)}
                  onRename={(next) => renameNode(current, next)}
                  onRemove={() => removeNode(current)}
                  onCreateStep={readOnly ? undefined : createLinked(current)}
                  onTest={api.testAction}
                  onProducts={api.products}
                  names={api.names}
                />
              ) : (
                <p className="rounded-xl border border-dashed border-(--color-line) px-4 py-12 text-center text-body text-(--color-muted)">
                  No steps yet. Add the first one — it becomes where the bot starts.
                </p>
              )}
            </div>
          )}

          {view === 'map' && (
            <FlowMap
              definition={definition}
              selected={current}
              onSelect={(id) => { setSelected(id); setView('steps'); }}
            />
          )}

          {view === 'settings' && (
            <Settings
              definition={definition}
              meta={meta}
              channels={channels}
              readOnly={readOnly}
              canDelete={Boolean(api.remove) && !readOnly}
              deleting={remove.isPending}
              onMeta={setMeta}
              onWrite={(next) => write(next, 'settings')}
              onDelete={() => remove.mutate()}
            />
          )}

          {view === 'json' && (
            <JsonView
              text={text}
              parses={parses}
              readOnly={readOnly}
              onChange={(next) => setText(next, 'definition')}
              onRevert={() => setText(flow.definition)}
              dirty={textDirty}
            />
          )}
        </div>

        {/* The emulator is the other half of the screen from 1280 up, and a panel you
            toggle below that — a phone-width builder has no room for two panes and
            hiding the flow to look at the test would be worse than a button. */}
        <div className={[
          'w-full shrink-0 xl:w-96 xl:sticky xl:top-4 2xl:w-112',
          showTest ? '' : 'hidden xl:block',
        ].join(' ')}>
          <FlowEmulator
            definition={text}
            title={meta.name || flow.name}
            rehearse={(body) => latest.current.rehearse(body)}
            disabled={!parses}
          />
        </div>
      </div>

      <p className="max-w-[90ch] text-small leading-relaxed text-(--color-muted)">
        Publishing freezes this version and opens the next one. Conversations already
        part-way through keep running the version they started on, so a change never
        lands on somebody mid-answer.
      </p>

      {leaving && (
        <Confirm
          title="Leave without saving?"
          body="The changes you made here will be lost."
          confirm="Leave"
          onCancel={() => setLeaving(false)}
          onConfirm={() => { setLeaving(false); onClose(); }}
        />
      )}
    </div>
  );
}

/** Which step a validator message is about, if it names one that exists. */
function nodeOf(line: string, nodes: Record<string, unknown>): string | null {
  const match = /Node '([^']+)'/.exec(line) ?? /points at unknown node '([^']+)'/.exec(line);
  const id = match?.[1];
  return id && nodes[id] ? id : null;
}

// ---------------------------------------------------------------- outline --

/**
 * The steps, in the order a customer meets them.
 *
 * The one place the whole flow is visible at once without being a diagram. Search
 * looks at names and at what a step says, because "the one that mentions the
 * warranty" is how people remember a step, not by its id.
 */
function Outline({ ordered, current, problems, start, readOnly, onSelect, onAdd }: {
  ordered: { id: string; node: FlowNode; unreachable: boolean }[];
  current: string | null;
  problems: Map<string, { errors: number; warnings: number }>;
  start?: string;
  readOnly: boolean;
  onSelect: (id: string) => void;
  onAdd: (type: FlowNodeType) => void;
}) {
  const [query, setQuery] = useState('');
  const [picking, setPicking] = useState(false);
  const rowRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLOListElement>(null);

  // Keeps the selected step in view by scrolling the list itself, not
  // `scrollIntoView`, which scrolls every ancestor — including the app shell's
  // overflow-hidden main, which then cannot be scrolled back.
  useEffect(() => {
    const list = listRef.current;
    const row = rowRef.current;
    if (!list || !row) return;
    const top = row.offsetTop;
    const bottom = top + row.offsetHeight;
    if (top < list.scrollTop) list.scrollTop = top - 8;
    else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight + 8;
  }, [current]);

  const q = query.trim().toLowerCase();
  const shown = q
    ? ordered.filter(({ id, node }) => id.toLowerCase().includes(q) || nodeSummary(node).toLowerCase().includes(q))
    : ordered;
  const firstUnreachable = shown.findIndex((n) => n.unreachable);

  return (
    <div className="min-w-0 md:sticky md:top-4">
      <div className="flex gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Find among ${ordered.length}`}
          aria-label="Find a step"
          className="min-w-0 flex-1 rounded-lg border border-(--color-line) bg-(--color-paper) px-3 py-2 text-body focus-visible:outline-none focus-visible:border-(--color-brand)"
        />
        {!readOnly && (
          <button type="button" onClick={() => setPicking((v) => !v)} className={PRIMARY} aria-expanded={picking}>
            {picking ? 'Close' : '＋ Step'}
          </button>
        )}
      </div>

      {picking && (
        <div className="mt-2 grid gap-1 rounded-xl border border-(--color-line) bg-(--color-surface) p-2 shadow-(--shadow-float)">
          {NODE_ORDER.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => { onAdd(type); setPicking(false); }}
              className="flex items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition hover:bg-(--color-sunken)"
            >
              <span className="mt-1.5 size-2.5 shrink-0 rounded-full" style={{ background: NODE_TONE[type] }} aria-hidden />
              <span className="min-w-0">
                <span className="block text-body font-medium">{NODE_LABEL[type]}</span>
                <span className="block text-small text-(--color-muted)">{NODE_EXPLAIN[type]}</span>
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Below md the outline is a picker, not a column: a phone has no room beside
          the form, and a list of sixty steps above it would push the form off screen. */}
      <label className="mt-2 block md:hidden">
        <span className="sr-only">Step</span>
        <select value={current ?? ''} onChange={(e) => onSelect(e.target.value)} className={`${FIELD} mt-0`}>
          {shown.map(({ id, node }) => (
            <option key={id} value={id}>{id} · {NODE_LABEL[(node.type as FlowNodeType) ?? 'message'] ?? node.type}</option>
          ))}
        </select>
      </label>

      <ol ref={listRef} className="pane-scroll relative mt-2 hidden max-h-[calc(100vh-14rem)] overflow-y-auto rounded-xl border border-(--color-line) bg-(--color-surface) md:block">
        {shown.length === 0 && (
          <li className="px-3 py-6 text-center text-small text-(--color-muted)">Nothing matches.</li>
        )}
        {shown.map(({ id, node, unreachable }, i) => {
          const type = (node.type as FlowNodeType) ?? 'message';
          const problem = problems.get(id);
          const active = id === current;
          return (
            <li key={id}>
              {i === firstUnreachable && (
                <p className="border-t border-(--color-line-dim) bg-(--color-paper) px-3 py-1.5 text-micro font-medium tracking-wide text-(--color-warn) uppercase">
                  Nothing leads here
                </p>
              )}
              <button
                ref={active ? rowRef : undefined}
                type="button"
                onClick={() => onSelect(id)}
                aria-current={active || undefined}
                className={[
                  'flex w-full items-start gap-2.5 border-t border-(--color-line-dim) px-3 py-2 text-left transition first:border-t-0',
                  active ? 'bg-(--color-brand-wash)' : 'hover:bg-(--color-sunken)',
                  unreachable ? 'opacity-80' : '',
                ].join(' ')}
              >
                <span className="mt-1.5 size-2.5 shrink-0 rounded-full" style={{ background: NODE_TONE[type] ?? 'var(--color-muted)' }} aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className={`truncate text-body ${active ? 'font-semibold' : 'font-medium'}`}>{id}</span>
                    {id === start && <span className="shrink-0 text-micro font-medium text-(--color-brand)">START</span>}
                    {problem && (
                      <span className={`ml-auto shrink-0 rounded-full px-1.5 text-micro font-semibold ${problem.errors ? 'bg-(--color-bad-wash) text-(--color-bad)' : 'bg-(--color-warn-wash) text-(--color-warn)'}`}>
                        {problem.errors || problem.warnings}
                      </span>
                    )}
                  </span>
                  <span className="block truncate text-small text-(--color-muted)">
                    {NODE_LABEL[type] ?? node.type}{nodeSummary(node) ? ` · ${nodeSummary(node)}` : ''}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// --------------------------------------------------------------- settings --

/**
 * What is not a step: the name, the number, where it starts, what wakes it, and
 * where a known customer is picked up.
 *
 * The resume block existed only in the JSON until now, which meant the one thing that
 * stops a returning customer being asked for their mobile number again was something
 * nobody could switch on without reading the interpreter.
 */
function Settings({ definition, meta, channels, readOnly, canDelete, deleting, onMeta, onWrite, onDelete }: {
  definition: FlowDefinition;
  meta: { name: string; channelId: number | null };
  channels?: FlowChannelOption[];
  readOnly: boolean;
  canDelete: boolean;
  deleting: boolean;
  onMeta: (next: { name: string; channelId: number | null }) => void;
  onWrite: (next: FlowDefinition) => void;
  onDelete: () => void;
}) {
  const ids = Object.keys(definition.nodes ?? {});
  const resume = (definition.resume ?? null) as Record<string, unknown> | null;
  const resumeOn = Boolean(resume && resume.state);
  const [confirming, setConfirming] = useState(false);

  const list = (value: unknown) => (Array.isArray(value) ? (value as unknown[]).map(String).join(', ') : '');
  const split = (value: string) => value.split(',').map((t) => t.trim()).filter(Boolean);
  const setResume = (patch: Record<string, unknown>) => onWrite({ ...definition, resume: { ...(resume ?? {}), ...patch } });

  return (
    <fieldset disabled={readOnly} className="flex flex-col gap-4">
      {readOnly && (
        <p className="rounded-lg border border-(--color-line) bg-(--color-surface) px-4 py-3 text-body text-(--color-ink-soft)">
          This version is published. Its settings are read only; change them on the draft.
        </p>
      )}

      <section className="rounded-xl border border-(--color-line) bg-(--color-surface) p-4 sm:p-5">
        <h2 className="text-lead font-semibold">About this bot</h2>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="text-small font-medium text-(--color-muted)">Name</span>
            <input value={meta.name} onChange={(e) => onMeta({ ...meta, name: e.target.value })} maxLength={80} className={FIELD} />
            <span className="mt-1 block text-small text-(--color-faint)">Only your team sees this.</span>
          </label>
          {channels && (
            <label className="block">
              <span className="text-small font-medium text-(--color-muted)">Answers on</span>
              <select value={meta.channelId ?? ''} onChange={(e) => onMeta({ ...meta, channelId: e.target.value ? Number(e.target.value) : null })} className={FIELD}>
                <option value="">Every number without its own bot</option>
                {channels.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
              <span className="mt-1 block text-small text-(--color-faint)">One bot can be live per number, plus one for the rest.</span>
            </label>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-(--color-line) bg-(--color-surface) p-4 sm:p-5">
        <h2 className="text-lead font-semibold">How it starts</h2>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="text-small font-medium text-(--color-muted)">First step</span>
            <select value={definition.start ?? ''} onChange={(e) => onWrite({ ...definition, start: e.target.value || undefined })} className={FIELD}>
              <option value="">Choose…</option>
              {ids.map((id) => <option key={id} value={id}>{id}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-small font-medium text-(--color-muted)">Wake words</span>
            <input
              value={list(definition.triggers)}
              onChange={(e) => onWrite({ ...definition, triggers: split(e.target.value) })}
              placeholder="hi, hello, menu"
              className={FIELD}
            />
            <span className="mt-1 block text-small text-(--color-faint)">
              Any of these, typed on its own, starts the bot again from the first step. Separate with commas.
            </span>
          </label>
        </div>
      </section>

      <section className="rounded-xl border border-(--color-line) bg-(--color-surface) p-4 sm:p-5">
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={resumeOn}
            onChange={(e) => {
              if (!e.target.checked) { onWrite({ ...definition, resume: undefined }); return; }
              setResume({ state: resume?.state ?? definition.start ?? ids[0] ?? '' });
            }}
            className="mt-1 size-4 accent-(--color-brand)"
          />
          <span>
            <span className="block text-lead font-semibold">Pick up where a known customer left off</span>
            <span className="block text-small text-(--color-muted)">
              When somebody the bot already identified comes back, skip the questions and go straight to a step.
              A wake word from the list below, or a long silence, still starts from the beginning.
            </span>
          </span>
        </label>

        {resumeOn && (
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-small font-medium text-(--color-muted)">Go straight to</span>
              <select value={String(resume?.state ?? '')} onChange={(e) => setResume({ state: e.target.value })} className={FIELD}>
                {ids.map((id) => <option key={id} value={id}>{id}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-small font-medium text-(--color-muted)">They are known when this is set</span>
              <input value={String(resume?.when_var ?? '')} onChange={(e) => setResume({ when_var: e.target.value || undefined })} placeholder="customer_id" className={`${FIELD} font-mono`} />
            </label>
            <label className="block">
              <span className="text-small font-medium text-(--color-muted)">Keep these values</span>
              <input value={list(resume?.keep_vars)} onChange={(e) => setResume({ keep_vars: split(e.target.value).length ? split(e.target.value) : undefined })} placeholder="customer_id, customer_name, mobile" className={`${FIELD} font-mono`} />
              <span className="mt-1 block text-small text-(--color-faint)">Everything else is forgotten between visits.</span>
            </label>
            <label className="block">
              <span className="text-small font-medium text-(--color-muted)">Words that start over instead</span>
              <input value={list(resume?.reset_triggers)} onChange={(e) => setResume({ reset_triggers: split(e.target.value).length ? split(e.target.value) : undefined })} placeholder="restart, change number" className={FIELD} />
            </label>
          </div>
        )}
      </section>

      {canDelete && (
        <section className="rounded-xl border border-(--color-bad)/40 p-4 sm:p-5">
          <h2 className="text-lead font-semibold">Delete this bot</h2>
          <p className="mt-1 max-w-[70ch] text-small text-(--color-muted)">
            It has never been published, so nothing refers to it. Once a bot has a published version it stays, because the conversations that ran on it point at it.
          </p>
          <button type="button" onClick={() => setConfirming(true)} disabled={deleting} className={`${BUTTON} mt-3 border-(--color-bad) text-(--color-bad)`}>
            {deleting ? 'Deleting…' : 'Delete bot'}
          </button>
          {confirming && (
            <Confirm
              title="Delete this bot?"
              body="Everything in it goes. This cannot be undone."
              confirm="Delete"
              danger
              onCancel={() => setConfirming(false)}
              onConfirm={() => { setConfirming(false); onDelete(); }}
            />
          )}
        </section>
      )}
    </fieldset>
  );
}

// --------------------------------------------------------------- problems --

/** The errors and warnings, told apart from "this is not JSON", each one a link to its step. */
function Problems({ parses, report, nodes, onSelect }: {
  parses: boolean;
  report: FlowReport | null;
  nodes: Record<string, unknown>;
  onSelect: (id: string) => void;
}) {
  const [open, setOpen] = useState(true);

  if (!parses) {
    return (
      <p className="mb-3 rounded-lg border border-(--color-bad) bg-(--color-bad-wash) px-4 py-3 text-body text-(--color-bad)">
        The definition is not valid JSON, so nothing below has been checked. Fix it on
        the Definition tab — Format will tell you where.
      </p>
    );
  }

  if (!report || (report.errors.length === 0 && report.warnings.length === 0)) return null;

  const line = (text: string, tone: string) => {
    const id = nodeOf(text, nodes);
    return (
      <li key={text} className={tone}>
        {id ? (
          <button type="button" onClick={() => onSelect(id)} className="text-left underline decoration-current/40 underline-offset-2 hover:decoration-current">
            {text}
          </button>
        ) : text}
      </li>
    );
  };

  const total = report.errors.length + report.warnings.length;

  return (
    <div className="mb-3 rounded-lg border border-(--color-line) bg-(--color-surface)">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-body" aria-expanded={open}>
        <span className={`inline-block transition ${open ? 'rotate-90' : ''}`}>▸</span>
        <span className="font-medium">{total === 1 ? 'One thing' : `${total} things`} to look at</span>
        <span className="text-small text-(--color-muted)">— click one to go to its step</span>
      </button>
      {open && (
        <ul className="flex list-disc flex-col gap-1.5 border-t border-(--color-line-dim) py-3 pr-4 pl-9 text-body">
          {report.errors.map((e) => line(e, 'text-(--color-bad)'))}
          {report.warnings.map((w) => line(w, 'text-(--color-ink-soft)'))}
        </ul>
      )}
    </div>
  );
}

// -------------------------------------------------------------- definition --

/**
 * The definition itself, with the three things anybody editing JSON by hand needs.
 *
 * Format is not a nicety: a definition pasted from somewhere else arrives on one line,
 * and a parse error in a one-line document reports a column number nobody can act on.
 * Formatting it first is how you find the stray comma.
 */
function JsonView({ text, parses, readOnly, dirty, onChange, onRevert }: {
  text: string;
  parses: boolean;
  readOnly: boolean;
  dirty: boolean;
  onChange: (next: string) => void;
  onRevert: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const lines = text.split('\n').length;
  const size = new Blob([text]).size;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" disabled={!parses || readOnly} onClick={() => onChange(JSON.stringify(JSON.parse(text), null, 2))} className={BUTTON}>
          Format
        </button>
        <button type="button" onClick={() => { void navigator.clipboard?.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }} className={BUTTON}>
          {copied ? 'Copied' : 'Copy'}
        </button>
        <button type="button" disabled={!dirty || readOnly} onClick={onRevert} className={BUTTON}>
          Discard changes
        </button>

        <span className="ml-auto font-mono text-small text-(--color-muted)">
          {lines} lines · {(size / 1024).toFixed(1)} KB · {parses ? 'valid JSON' : 'not valid JSON'}
        </span>
      </div>

      <textarea
        value={text}
        onChange={(e) => onChange(e.target.value)}
        readOnly={readOnly}
        spellCheck={false}
        wrap="off"
        rows={28}
        aria-label="Flow definition"
        className={[
          'w-full rounded-lg border bg-(--color-paper) p-4 font-mono text-small leading-relaxed focus-visible:outline-none',
          parses ? 'border-(--color-line) focus-visible:border-(--color-brand)' : 'border-(--color-bad)',
        ].join(' ')}
      />

      <p className="text-small text-(--color-muted)">
        The definition the interpreter runs. Checked as you type by the same rules that
        decide whether it can be published, and the test on the right runs whatever is
        in here — including changes you have not saved.
      </p>
    </div>
  );
}
